/**
 * Cross-domain auth helpers — each frontend origin has its own localStorage token.
 * Both app.fundelite.ai and app.fundelitehq.com call https://api.fundelite.ai.
 */

export { AGENCY_APP_URL, PLATFORM_APP_URL } from './hostnamePortal';

export {
  AGENCY_DASHBOARD_PATH,
  HOST_SAFE_AUTH_PATHS,
  isAgencyWorkspaceUser,
  isAgencyOwnerUser,
  isPlatformStaffUser,
  isHostSafeAuthPath,
  agencyOwnerNeedsEmailVerification,
  getDashboardForUser,
  resolvePostAuthTarget,
  applyPostAuthRedirect,
  resolvePortalBoundaryRedirect,
  logRouteResolver,
  isSameNavigationTarget,
  hasAgencyAccess,
  hasValidAgencyId,
  isAgencyUser,
  isClientPortalUser,
  isManagedClientUser,
  isPlatformAdmin,
  isSelfRepairUser,
  getClientPortalDashboardPath,
} from './routeResolver';

export {
  FUNDGENIUS_HQ_CLIENTS_PATH,
  clearStaleAuthRedirectStorage,
  getFundGeniusPostLoginPath,
  getPostSignupRedirectPath,
  isFundGeniusAppLoginHost,
  isFundGeniusSignupHost,
  sanitizeAuthRedirectUrl,
  FUNDGENIUS_APP_CLIENTS_PATH,
} from './fundgeniusLoginBranding';

export {
  getPortalLandingRoute,
  FUNDELITE_PLATFORM_LANDING,
  FUNDGENIUS_PLATFORM_LANDING,
  isFundElitePlatformLandingHost,
  isFundGeniusLandingHost,
  isFundGeniusPath,
} from './portalLandingRoute';

export const AUTH_TOKEN_KEY = 'fundelite_token';

export const PUBLIC_AUTH_API_PATHS = [
  '/auth/login',
  '/auth/signup',
  '/auth/signup/agency',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/reset-password/verify',
  '/auth/session',
  '/auth/google/token-login',
  // Refresh uses the HttpOnly session cookie; omit stale Authorization headers.
  '/auth/refresh',
];

/** Persist token/user using the same storage contract as login. */
export function persistAuthSession({ token, user }) {
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
  return { token, user };
}

/** Normalize backend base URL (no trailing /api). */
export function getApiBaseUrl() {
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
  return raw.replace(/\/api$/, '');
}

export function getApiUrl() {
  return `${getApiBaseUrl()}/api`;
}

/** True when request must not carry a stale Authorization header. */
export function isPublicAuthRequestUrl(url) {
  const u = String(url || '');
  return PUBLIC_AUTH_API_PATHS.some((path) => u.includes(path));
}

export function normalizeLoginEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Clear auth for the current browser origin only (localStorage is per-domain). */
export function clearLocalAuthState() {
  localStorage.removeItem(AUTH_TOKEN_KEY);
}

/**
 * Clear account-scoped client caches after switching workspaces.
 * Keeps the auth token, language, and theme preferences.
 */
export function clearAccountScopedClientState() {
  if (typeof window === 'undefined') return;
  const preserve = new Set([
    AUTH_TOKEN_KEY,
    'fundelite_language',
    'fundelite_theme',
    'fundelite-theme',
  ]);
  const accountScopedExact = [
    'recentContexts',
    'pinnedContexts',
    'returnTo',
    'previousRoute',
    'redirectTo',
    'funding-clients-sample-mode',
    'fundelite_selected_account_id',
    'active_account',
    'active_account_id',
    'account_context',
    'selected_account',
    'selected_account_id',
    'portal_type',
    'account_type',
    'portal_access',
    'last_portal',
    'redirect_path',
    'auth_user',
  ];
  for (const key of accountScopedExact) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key) keys.push(key);
    }
    for (const key of keys) {
      if (preserve.has(key)) continue;
      if (
        key.startsWith('funding-client-drafts:')
        || key.startsWith('agency_')
        || key.includes('entitlement')
        || key.includes('branding')
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
  if (typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent('fundelite:account-switched'));
  }
}

/**
 * Resolve safe post-login path on the current origin.
 * Supports React Router location objects, relative paths, and same-origin absolute URLs.
 */
export function resolveLoginReturnPath(fromLocation, currentOrigin = window.location.origin) {
  if (!fromLocation) return null;

  let path = null;

  if (typeof fromLocation === 'string') {
    const raw = fromLocation.trim();
    if (!raw) return null;
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      try {
        const url = new URL(raw);
        if (url.origin !== currentOrigin) return null;
        path = `${url.pathname}${url.search}${url.hash}`;
      } catch {
        return null;
      }
    } else if (raw.startsWith('/')) {
      path = raw;
    } else {
      return null;
    }
  } else {
    const pathname = fromLocation.pathname || '';
    const search = fromLocation.search || '';
    const hash = fromLocation.hash || '';
    path = `${pathname}${search}${hash}`;
  }

  if (!path || path === '/login' || path === '/') return null;
  return path;
}

/** Google OAuth start URL with optional safe next path. */
export function getGoogleOAuthStartUrl(nextPath) {
  const base = `${getApiUrl()}/auth/google/start`;
  const next = resolveLoginReturnPath(nextPath, window.location.origin);
  if (!next) return base;
  return `${base}?${new URLSearchParams({ next }).toString()}`;
}

/** Friendly message for Google OAuth errors returned via login query params. */
export function getGoogleAuthErrorMessage(errorCode, detail) {
  if (errorCode === 'google_auth_denied') {
    return 'Google sign-in was cancelled. You can try again or use email and password.';
  }
  if (detail) {
    return detail;
  }
  if (errorCode === 'google_auth_failed') {
    return 'Google sign-in failed. Please try again or use email and password.';
  }
  return 'Sign-in failed. Please try again.';
}

/** Map axios/fetch errors to user-facing signup messages (includes FastAPI validation arrays). */
export function getSignupErrorMessage(error) {
  if (!error?.response) {
    return 'Unable to reach registration service. Please try again.';
  }

  const detail = error.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        const field = Array.isArray(item.loc) ? item.loc.filter(Boolean).join('.') : '';
        const message = item.msg || item.message || '';
        return field ? `${field}: ${message}` : message;
      })
      .filter(Boolean)
      .join(' ');
  }

  if (detail && typeof detail === 'object') {
    if (detail.code === 'DELETED_ACCOUNT_EMAIL_LOCKED' && detail.message) {
      return detail.message;
    }
    return detail.message || detail.error || JSON.stringify(detail);
  }

  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  if (error.response.status >= 500) {
    return 'Server error. Please contact support.';
  }

  return 'Registration failed. Please try again.';
}

/** Map axios/fetch errors to user-facing login messages (never generic "Invalid credentials"). */
export function getLoginErrorMessage(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;

  if (!error?.response) {
    return 'Login failed. Please try again.';
  }
  if (status === 403) {
    return (
      detail ||
      'This account was created with Google. Please continue with Google or reset your password.'
    );
  }
  if (status === 401) {
    return detail || 'Invalid email or password.';
  }
  if (status >= 500) {
    return 'Server error. Please contact support.';
  }
  return detail || 'Sign-in failed. Please try again.';
}
